import React from 'react';
import { openPrintWindow } from '../../utils/printDocument';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
} from '@mui/material';
import {
  Print as PrintIcon,
  ArrowBack,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const TimeTable = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString;
  };

  // Ordre des jours de la semaine
  const dayOrder = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  
  // Fonction pour trier les jours
  const sortDays = (schedule) => {
    if (!schedule) return [];
    return Object.entries(schedule)
      .sort((a, b) => {
        const indexA = dayOrder.indexOf(a[0]);
        const indexB = dayOrder.indexOf(b[0]);
        return indexA - indexB;
      });
  };

  const handlePrint = () => {
    const extraCss = `
            .print-page {
              line-height: 1.6;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            table, th, td {
              border: 1px solid #ddd;
            }
            th, td {
              padding: 12px;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
            }
            .tasks {
              margin-top: 10px;
              padding-left: 20px;
            }
            .schedule-title {
              color: #1976d2;
              margin-top: 30px;
              font-weight: bold;
            }
            .schedule-b-title {
              color: #9c27b0;
              margin-top: 30px;
              font-weight: bold;
            }
            .page-break {
              page-break-before: always;
            }
    `;

    const bodyHtml = `
          <div class="print-page">
          <div class="header">
            <h2>${userData?.hasAlternateSchedule ? "Mon Emploi du temps - Semaines alternées" : "Mon Emploi du temps"}</h2>
            <h3>${userData?.firstName} ${userData?.lastName}</h3>
            <p>Département: ${userData?.department}</p>
          </div>

          ${userData?.hasAlternateSchedule ? `
            <h3 class="schedule-title">Semaine A</h3>
          ` : ''}

          <table>
            <thead>
              <tr>
                <th>Jour</th>
                <th>Matin</th>
                <th>Après-midi</th>
                <th>Tâches</th>
              </tr>
            </thead>
            <tbody>
              ${sortDays(userData?.schedule || {}).map(([day, schedule]) => `
                <tr>
                  <td>${day}</td>
                  <td>${schedule.withBreak 
                    ? formatTime(schedule.morning?.start) + ' - ' + formatTime(schedule.morning?.end)
                    : formatTime(schedule.continuous?.start) + ' - ' + formatTime(schedule.continuous?.end)
                  }</td>
                  <td>${schedule.withBreak 
                    ? formatTime(schedule.afternoon?.start) + ' - ' + formatTime(schedule.afternoon?.end)
                    : '-'
                  }</td>
                  <td>
                    <ul class="tasks">
                      ${(schedule.tasks || []).map(task => `
                        <li>${task}</li>
                      `).join('')}
                    </ul>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          ${userData?.hasAlternateSchedule && userData?.scheduleB ? `
            <div class="page-break"></div>
            <div class="header">
              <h2>Mon Emploi du temps - Suite</h2>
              <h3>${userData?.firstName} ${userData?.lastName}</h3>
              <p>Département: ${userData?.department}</p>
            </div>
            
            <h3 class="schedule-b-title">Semaine B</h3>
            <table>
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Matin</th>
                  <th>Après-midi</th>
                  <th>Tâches</th>
                </tr>
              </thead>
              <tbody>
                ${sortDays(userData?.scheduleB || {}).map(([day, schedule]) => `
                  <tr>
                    <td>${day}</td>
                    <td>${schedule.withBreak 
                      ? formatTime(schedule.morning?.start) + ' - ' + formatTime(schedule.morning?.end)
                      : formatTime(schedule.continuous?.start) + ' - ' + formatTime(schedule.continuous?.end)
                    }</td>
                    <td>${schedule.withBreak 
                      ? formatTime(schedule.afternoon?.start) + ' - ' + formatTime(schedule.afternoon?.end)
                      : '-'
                    }</td>
                    <td>
                      <ul class="tasks">
                        ${(schedule.tasks || []).map(task => `
                          <li>${task}</li>
                        `).join('')}
                      </ul>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : ''}
          </div>`;

    openPrintWindow('Mon Emploi du temps', bodyHtml, extraCss, 'height=600,width=800', { singlePage: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/personnel/dashboard')}
        sx={{ mb: 2 }}
        variant="outlined"
      >
        Retour au tableau de bord
      </Button>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            {userData?.hasAlternateSchedule ? "Mon Emploi du temps - Semaines alternées" : "Mon Emploi du temps"}
          </Typography>
          <IconButton
            color="primary"
            onClick={handlePrint}
            title="Imprimer l'emploi du temps"
          >
            <PrintIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">
            {userData?.firstName} {userData?.lastName}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Département: {userData?.department}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Total hebdomadaire: {userData?.totalHours || '0h00'}
            {userData?.hasAlternateSchedule && userData?.totalHoursB && (
              <span> (Semaine A) / {userData.totalHoursB} (Semaine B)</span>
            )}
          </Typography>
        </Box>

        {/* Emploi du temps Semaine A */}
        <Box sx={{ mb: 4 }}>
          {userData?.hasAlternateSchedule && (
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Semaine A
            </Typography>
          )}
          
          <TableContainer component={Paper} sx={{ 
            border: userData?.hasAlternateSchedule ? '1px solid' : 'none',
            borderColor: 'primary.light',
            borderRadius: 1
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: userData?.hasAlternateSchedule ? 'primary.light' : 'inherit' }}>
                  <TableCell>Jour</TableCell>
                  <TableCell>Matin</TableCell>
                  <TableCell>Après-midi</TableCell>
                  <TableCell>Tâches</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortDays(userData?.schedule || {}).map(([day, schedule]) => (
                  <TableRow key={day}>
                    <TableCell component="th" scope="row">
                      {day}
                    </TableCell>
                    <TableCell>
                      {schedule.withBreak
                        ? `${formatTime(schedule.morning?.start)} - ${formatTime(schedule.morning?.end)}`
                        : `${formatTime(schedule.continuous?.start)} - ${formatTime(schedule.continuous?.end)}`
                      }
                    </TableCell>
                    <TableCell>
                      {schedule.withBreak
                        ? `${formatTime(schedule.afternoon?.start)} - ${formatTime(schedule.afternoon?.end)}`
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {schedule.tasks?.map((task, index) => (
                          <li key={index}>{task}</li>
                        ))}
                      </ul>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={schedule.active ? "Actif" : "Inactif"}
                        color={schedule.active ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Emploi du temps Semaine B */}
        {userData?.hasAlternateSchedule && userData?.scheduleB && (
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main', fontWeight: 'bold' }}>
              Semaine B
            </Typography>
            
            <TableContainer component={Paper} sx={{ 
              border: '1px solid',
              borderColor: 'secondary.light',
              borderRadius: 1
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'secondary.light' }}>
                    <TableCell>Jour</TableCell>
                    <TableCell>Matin</TableCell>
                    <TableCell>Après-midi</TableCell>
                    <TableCell>Tâches</TableCell>
                    <TableCell>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortDays(userData?.scheduleB || {}).map(([day, schedule]) => (
                    <TableRow key={day}>
                      <TableCell component="th" scope="row">
                        {day}
                      </TableCell>
                      <TableCell>
                        {schedule.withBreak
                          ? `${formatTime(schedule.morning?.start)} - ${formatTime(schedule.morning?.end)}`
                          : `${formatTime(schedule.continuous?.start)} - ${formatTime(schedule.continuous?.end)}`
                        }
                      </TableCell>
                      <TableCell>
                        {schedule.withBreak
                          ? `${formatTime(schedule.afternoon?.start)} - ${formatTime(schedule.afternoon?.end)}`
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          {schedule.tasks?.map((task, index) => (
                            <li key={index}>{task}</li>
                          ))}
                        </ul>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.active ? "Actif" : "Inactif"}
                          color={schedule.active ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default TimeTable;
